/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2020, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import * as _ from 'underscore';
import { Logger, MessageService } from '@core/lib/services';
import { Injectable, Injector } from '@angular/core';
import { NotifyService } from '@app/common/notify.service';
import { DiService } from '@app/di/di.service';
import { UserPreferenceService } from '@app/common/user-preference.service';
import { DiConfigService } from '@app/di/di-config.service';
import { appConstants } from '@app/common/app-constants';
import { TranslateService } from '@ngx-translate/core';
import { UtilService } from '@app/util/util.service';
import { IntegrationStatusService } from '@app/di/integration-status.service';
import { EventMessage, Events, EventService, EventType } from '@app/common/event';
import { UserPrivilegesService } from '@app/common/user-privileges.service';
import { AdvanceSearchService } from '@app/common/services/advance-search/advance-search.service';
import { XmlTemplateManagerService } from '@app/di/channel/lineshaft/xml-template-manager.service';
import { GenericReportingService } from '@app/di/integrations/reporting/generic-reporting.service';
import { PsReportingService } from '@app/di/integrations/reporting/ps-reporting.service';
import { ReportContextService } from '@app/di/integrations/reporting/report-context.service';
import { ReportSupportService } from '@app/di/integrations/reporting/report-support.service';
import { SRService } from '@app/di/integrations/reporting/smartreports/sr-reporting.service';
import { NPRDataService } from '@app/di/integrations/utils/npr-bridge.service';
import { SearchFilterCriteriaService } from '@app/ui/search/search-filter-criteria.service';
import { P2pNotificationService } from '@app/common/p2p-notification.service';
import { AppConfigService } from '@app/common/app-config.service';
import { ViewerIntegrationService } from '@app/di/integrations/viewer/viewer-integration.service';
import { LineShaftHandlerService } from '@app/di/channel/lineshaft/line-shaft-handler.service';
import {
  PerformanceMonitorService,
  PerformanceSource
} from '@app/ui/masthead/performance-monitor/performance-monitor.service';

const log = new Logger('ReportIntegrationService');

@Injectable({
  providedIn: 'root'
})
export class ReportIntegrationService {
  private reportingService;
  private lineShaftHandlerService: LineShaftHandlerService;

  private loadStatus;
  private isIntegrationPossible = false;
  private isNativeReporting = false;
  private hasXmlConfigError = false;
  // Holds the instance of OpenReportStuck Modal
  private moduleName;
  private refresh = false;

  constructor(
    private injector: Injector,
    private diConfigService: DiConfigService,
    private notify: NotifyService,
    private translate: TranslateService,
    private eventService: EventService,
    private messageService: MessageService,
    private appConfigService: AppConfigService,
    private utilService: UtilService,
    private reportSupportService: ReportSupportService,
    private reportContextService: ReportContextService,
    private diService: DiService,
    private userPreferenceService: UserPreferenceService,
    private userPrivilegesService: UserPrivilegesService,
    private advanceSearchService: AdvanceSearchService,
    private xmlTemplateManagerService: XmlTemplateManagerService,
    private integrationStatusService: IntegrationStatusService,
    private searchFilterCriteriaService: SearchFilterCriteriaService,
    private p2pService: P2pNotificationService,
    private nprDataService: NPRDataService,
    private viewerIntegrationService: ViewerIntegrationService
  ) {}

  initialize() {
    log.info('ReportIntegration Initializing...');
    if (this.integrationStatusService.isLineShaftIntegrationEnabled()) {
      // Communication channel
      this.lineShaftHandlerService = this.injector.get(LineShaftHandlerService);
      this.lineShaftHandlerService.registerCleanupCallback(this.cleanup);
      this.listenersForLineShaftChannel();
    }

    // Listeners
    this.broadcastListeners();
    this.beforeEvents();
    this.afterEvents();

    // Others
    this.loadStatus = appConstants.pluginLoadStatus.None;
    this.moduleName = this.diConfigService.REPORTING_CONFIG.MODULENAME;
    this.isNativeReporting = this.integrationStatusService.isNativeReportingEnabled();
    this.reportSupportService.initialize();

    if (this.isNativeReporting) {
      this.setReportingService();
      this.load();
    }

    this.p2pService.onMessage('ReportSync', info => {
      log.debug('Got ReportSync via P2P message', JSON.stringify(info));
      this.messageService.broadcast('ReportSync', info);
    });

    this.p2pService.onMessage('OwnershipAcquired', data => {
      this.setTabExamContext(data);
      this.notify.warning(
        this.translate.instant('ownership.acquired.message', {
          ACC: data.exam.accessions.join(','),
          USER: data.sender
        }),
        this.translate.instant('di.desktopIntegration.title')
      );
    });
  }

  private setTabExamContext(data) {
    _.each(data.exam.ids, id => {
      const tab = this.utilService.getTab(id);
      if (tab && !tab.active && tab.examContext === 'read') {
        tab.examContext =
          tab.workItem && tab.workItem.keys && tab.workItem.keys.disableAutoLaunchViewer
            ? Events.preview.name
            : Events.launchreadonly.name;
        this.messageService.broadcast('setExamMode', { id: tab.id, examContext: tab.examContext });
      }
    });
  }

  public hasSettings(): boolean {
    return this.reportingService && this.reportingService.showSettings;
  }

  public showSettings() {
    if (this.hasSettings()) {
      this.reportingService.showSettings();
    }
  }

  public showAdminPanel() {
    if (this.hasSettings()) {
      this.reportingService.showAdminSettings();
    }
  }

  private setReportingService() {
    // Read reportType query parameter at the beginning of the method
    const reportType_qp = this.utilService.getQueryParam('reportType');
    const configReportingType = this.diConfigService.DI_CONFIG.REPORTING_INTEGRATION_TYPE;

    console.log(
      '[setReportingService] reportType_qp:',
      reportType_qp,
      '| config:',
      configReportingType,
      '| isNativeReporting:',
      this.isNativeReporting
    );

    if (this.userHasPrivilege()) {
      if (
        reportType_qp === 'gr' ||
        this.integrationStatusService.isReportingType(appConstants.reportingAppName.Generic)
      ) {
        console.log('[setReportingService] -> GR');
        log.info('setReportingService - set GPRIService as  reportingService');
        this.reportingService = this.injector.get(GenericReportingService);
        this.reportingService.initialize();
        this.nprDataService.init('UseSystemToken');
        // Check generic reporting setcontext xml template is missing
        this.hasXmlConfigError = this.diService.hasXMLConfError('GenericReporting');
      } else if (
        reportType_qp === 'ps' ||
        this.integrationStatusService.isReportingType(appConstants.reportingAppName.PS)
      ) {
        console.log('[setReportingService] -> PS');
        log.info('setReportingService - set PSRIService as  reportingService');
        this.reportingService = this.injector.get(PsReportingService);
        this.reportingService.initialize();
        this.nprDataService.init('UseSystemToken');
        // Check PowerScribe setcontext xml template is missing
        this.hasXmlConfigError = this.diService.hasXMLConfError('PS360');
      } else if (reportType_qp === 'sr' || this.isNativeReporting) {
        console.log('[setReportingService] -> SR');
        this.reportingService = this.injector.get(SRService);
        this.reportingService.initialize();
      } else {
        console.log(
          '[setReportingService] -> No match | reportType_qp:',
          reportType_qp,
          '| config:',
          configReportingType
        );
        log.error('Unsupported reporting type = ' + this.diConfigService.DI_CONFIG.REPORTING_INTEGRATION_TYPE);
      }
    } else {
      console.warn('[setReportingService] -> No privilege | config:', configReportingType);
      log.warn('User has no privilege to use ' + this.diConfigService.DI_CONFIG.REPORTING_INTEGRATION_TYPE);
    }
  }

  isServiceDown() {
    if (this.reportingService && this.reportingService.isServiceDown) {
      return this.reportingService.isServiceDown();
    }
    return false;
  }

  private userHasPrivilege() {
    let isPrivilegedUser = false;
    if (
      (this.isNativeReporting && this.userPrivilegesService.hasPrivilege('ID_NPR')) ||
      this.userPrivilegesService.hasPrivilege('Dictate')
    ) {
      isPrivilegedUser = true;
    }
    if (!isPrivilegedUser) {
      log.info('userHasPrivilege - isPrivilegedUser : ', isPrivilegedUser);
    }
    // debug will be changed to info after using message service after angular js to angular migration
    // no need to notify if isPrivilegedUser=true since log level will be info
    log.debug('userHasPrivilege - isPrivilegedUser : ', isPrivilegedUser);
    return isPrivilegedUser;
  }

  private isPluginLoaded(currentAction?) {
    let status = false;
    if (this.loadStatus === appConstants.pluginLoadStatus.PluginLoaded) {
      status = true;
    } else if (this.loadStatus === appConstants.pluginLoadStatus.LoadPluginInitiated) {
      log.debug('isPluginLoaded - reporting initialization is in progress.');
      if (currentAction && currentAction !== 'preview' && currentAction !== 'closediscard') {
        this.notify.warning(
          this.translate.instant('di.desktopIntegration.warning.dictationIsInitializing'),
          this.translate.instant('di.desktopIntegration.title')
        );
      }
    }
    // No need to notify user if pluginLoadStatus = None or PluginLoad since user is already notified
    log.info('isPluginLoaded - ' + status);
    return status;
  }

  private doReportingIntegration() {
    let reportingIntegration = true;
    if (this.diConfigService.DI_CONFIG.REPORTING_INTEGRATION_TYPE === appConstants.reportingAppName.None) {
      reportingIntegration = false;
      log.info(
        'doReportingIntegration - REPORTING_CONFIG.INTEGRATION_TYPE = ' +
          this.diConfigService.DI_CONFIG.REPORTING_INTEGRATION_TYPE
      );
    } else if (_.isUndefined(this.reportingService)) {
      reportingIntegration = false;
      log.info('doReportingIntegration - reportingService is undefined ');
    } else if (!this.userHasPrivilege()) {
      reportingIntegration = false;
      log.info('doReportingIntegration - user does not have dictation privilege');
    } else if (!this.diService.isSupportedBrowser()) {
      reportingIntegration = false;
      log.info('doReportingIntegration - unsupported browser');
    }
    // debug will be changed to info after using message service after angular js to angular migration
    // No need to notify user reportingIntegration's value if log level is info
    log.debug('doReportingIntegration - reporting integration :  ', reportingIntegration);
    return reportingIntegration;
  }

  private loadPlugin() {
    let status = false;
    try {
      // Subscribe for response and event by setting scope function as callback
      if (!this.isNativeReporting) {
        this.reportingService.subscribeResponseAndEvents(xml => {
          this.diService.loadPluginsInvokeAPIWrapperExecute(xml);
        });
      }
      this.reportingService.setCheckViewerContextCallback(contextId => {
        return this.viewerIntegrationService.checkViewerExistAndClosable(contextId);
      });
      this.reportingService.setCloseViewerCallback((actionName, contextId) => {
        return this.viewerIntegrationService.closeViewer(actionName, contextId);
      });
      this.reportSupportService.setHideAllOpenViewersCallback(() => {
        this.viewerIntegrationService.hideAllOpenViewers();
      });

      // Load reporting plugin
      if (this.isNativeReporting) {
        this.isIntegrationPossible = true;
        this.loadStatus = appConstants.pluginLoadStatus.PluginLoaded;
        this.integrationStatusService.setReportingPluginStatus(true);
      } else {
        this.sendLoadPluginXML();
        // loadStatus is set to failed on UV client download cancel scenario, loadStatus is checked against none
        // so as to ensure no other status is set, and to avoid showing warning/error messages in launch/preview
        // after failure.
        if (this.loadStatus === appConstants.pluginLoadStatus.None) {
          this.loadStatus = appConstants.pluginLoadStatus.LoadPluginInitiated;
        }
      }
      status = true;
    } catch (error) {
      log.error('loadPlugin() failed :' + error);
    }
    return status;
  }

  private load() {
    if (!this.loadPlugin()) {
      let loadPluginsInvokeCnt = 0;
      // Invoke this function repeatedly until loadPlugin returns true or loadPluginsInvokeCnt = 3
      const loadPluginInterval = setInterval(() => {
        loadPluginsInvokeCnt++;
        if (this.loadPlugin()) {
          clearInterval(loadPluginInterval);
        } else if (loadPluginsInvokeCnt === 3) {
          log.error('onDILoadPlugins - LineShaft did not start. loadPluginsInvokeCnt: ' + loadPluginsInvokeCnt);
          clearInterval(loadPluginInterval);
          this.notify.critical(
            this.translate.instant('di.desktopIntegration.critical.loadDictateFailed'),
            this.translate.instant('di.desktopIntegration.titleDictateDisabled')
          );
        }
      }, 500);
    } else {
      log.info('Lineshaft has started in first attempt');
    }
  }

  private handlePluginManagerLoadResponse(response) {
    if (response.resultValue === 'FAILURE') {
      // Plugin rename error scenario: In this case there is no response from corresponding plugin. So whenever
      // there is a load failure message from LSPluginManager, Desktop Integration need to clear timer and
      // process next request from queue. This is implemented by comparing module name in response and
      // messageWaitingForResponse.
      // This scenarios is handled in parent controller's LSPluginManagerResponseHandler() function.

      // No need to display notification message if load status is already marked as pluginLoadFailed.
      if (this.loadStatus !== appConstants.pluginLoadStatus.PluginLoadFailed) {
        this.notify.critical(
          this.translate.instant('di.desktopIntegration.critical.loadDictateFailed'),
          this.translate.instant('di.desktopIntegration.titleDictateDisabled')
        );
      }
      this.loadStatus = appConstants.pluginLoadStatus.PluginLoadFailed;
      log.error('handlePluginManagerLoadResponse - plugin load failed and dictation disabled');
    }
  }

  private handlePluginLoadResponse(response) {
    if (response.resultValue === 'SUCCESS') {
      if (this.loadStatus === appConstants.pluginLoadStatus.PluginLoadFailed) {
        log.info('handlePluginLoadResponse : reporting plugin load success after timeout');
        this.notify.success(
          this.translate.instant('di.desktopIntegration.success.loadDictationSuccessAfterTimeout'),
          this.translate.instant('di.desktopIntegration.titleDictationEnabled')
        );
      }
      this.loadStatus = appConstants.pluginLoadStatus.PluginLoaded;
      log.info('handlePluginLoadResponse : plugin load success.');
    }
    if (response.resultValue === 'FAILURE') {
      log.error('handlePluginLoadResponse - plugin load failed - Failure reason : ' + response.resultMsg);
      // No need to display dictation disabled critical message if it is already displayed from plugin load
      // timeout code block
      if (this.loadStatus !== appConstants.pluginLoadStatus.PluginLoadFailed) {
        this.notify.critical(
          this.translate.instant('di.desktopIntegration.critical.loadDictateFailed'),
          this.translate.instant('di.desktopIntegration.titleDictateDisabled')
        );
      }
      this.loadStatus = appConstants.pluginLoadStatus.PluginLoadFailed;
    }
  }

  private readActionHandler(target) {
    if (this.diService.getApplicationContext().toString() !== target.id.toString()) {
      log.warn(
        'on read action - context (' +
          target.id +
          ') does not match with active' +
          ' work item (' +
          this.diService.getApplicationContext() +
          '), ignore launch'
      );
      return;
    }
    // If read action is fired from diagnostic-hub launch dictation without checking the dictation preference
    // and exam status
    if (target.keys.source === 'diagnostic-hub') {
      log.info('on read action : read request from diagnostic-hub, trigger launchDictation action');

      this.eventService.sendEvent(
        new EventMessage(Events.launchDictation, {
          workItem: target
        })
      );
      return;
    }

    if (!this.isNativeIntegration() && _.isEmpty(target.keys.procedureAccessionNo)) {
      this.reportingService.preview(target);
      PerformanceMonitorService.clear(PerformanceSource.REPORT);

      log.error('read : procedureAccessionNo is empty, cannot open the report.');
      this.notify.warning(
        this.translate.instant('di.desktopIntegration.warning.emptyAccession'),
        this.translate.instant('di.desktopIntegration.title')
      );
      this.reportSupportService.releaseReadLock(target.id);
    } else if (target.keys.launchDictation) {
      this.reportingService.read(target);
    } else {
      log.debug('read : save and close previous report');
      this.reportingService.preview(target);
      PerformanceMonitorService.clear(PerformanceSource.REPORT);
    }
  }

  private resetApplicationState(context) {
    const pluginLoadStatus = this.isPluginLoaded();
    if (!this.isIntegrationPossible || !pluginLoadStatus) {
      log.info(
        'close/clear : skip action - isIntegrationPossible : ' +
          this.isIntegrationPossible +
          ', isPluginLoaded : ' +
          pluginLoadStatus
      );
      return;
    }
    this.reportContextService.clearDxHubContext(context.id);
    // Change PowerScribe state based on whether application conetxt present or not
    this.reportingService.resetApplicationState(context);
  }

  private checkIntegrationState(currentAction?) {
    const pluginLoadStatus = this.isPluginLoaded(currentAction);
    if (!this.isIntegrationPossible || !pluginLoadStatus) {
      log.info(
        'checkIntegrationState : isIntegrationPossible : ' +
          this.isIntegrationPossible +
          ', isPluginLoaded : ' +
          pluginLoadStatus
      );
      return false;
    }
    if (this.reportingService.getDictationStatus() === appConstants.dictationIntegrationStatus.Disabled) {
      log.info('checkIntegrationState - dictation integration is disabled');
      return false;
    }
    if (this.reportingService.isIntegrationLoading(currentAction)) {
      log.info('checkIntegrationState - dictation integration is loading');
      return false;
    }
    return true;
  }

  private closeDiscardReport(WorkItem) {
    try {
      if (!this.checkIntegrationState()) {
        return;
      }
      //Dx hub closure
      this.reportContextService.clearDxHubContext(WorkItem.id);
      PerformanceMonitorService.clear(PerformanceSource.REPORT);
      this.reportingService.closeDiscardReport(WorkItem);
    } catch (error) {
      log.error('failed on closediscard event with ' + error.description);
    }
  }

  private cleanup = (): boolean => {
    try {
      // Skip cleanup if integration not possible or plugin not loaded
      if (!this.isIntegrationPossible || this.loadStatus === appConstants.pluginLoadStatus.None) {
        return;
      }
      log.info('CleanUp - Started');
      this.reportingService.cleanup();
      return true;
    } catch (error) {
      log.error('CleanUp - Error >> ', error);
      return false;
    }
  };

  private async onDILoadPlugin() {
    log.info('onDILoadPlugin');
    if (this.isNativeReporting) {
      return;
    }
    if (this.loadStatus !== appConstants.pluginLoadStatus.None) {
      // Plugin load status becomes PluginLoadFailed if failed to UV client download.
      log.error('Load plugin status is not none, loadStatus:' + this.loadStatus);
      return;
    }
    this.setReportingService();
    // Check whether reporting integration is disabled
    if (this.reportingService?.getDictationStatus() === appConstants.dictationIntegrationStatus.Disabled) {
      log.error('onDILoadPlugin - dictation integration is disabled');
      return;
    }
    // Check whether reporting integration is possible here as it is the first function gets executed
    this.isIntegrationPossible = this.doReportingIntegration();
    if (!this.isIntegrationPossible) {
      return;
    }

    // Configuration and xml template validation
    if (!this.reportingService.isDictationConfigValid() || this.hasXmlConfigError) {
      log.error('onDILoadPlugin - Dictation configuration invalid');
      this.notify.critical(
        this.translate.instant('di.desktopIntegration.critical.incorrectDictateConfig'),
        this.translate.instant('di.desktopIntegration.titleDictateDisabled')
      );
      return;
    }
    this.load();
  }

  private onDisableIntegration() {
    // Setting the load status to failed to handle the below scenarios in which desktop integration is disabled
    // 1. UV client download is cancelled
    // 2. InvokeAPI method crashes
    // 3. Context switch failure
    this.loadStatus = appConstants.pluginLoadStatus.PluginLoadFailed;
    this.reportSupportService.clearMessageQueue();
  }

  private onHandleLineShaftMessage(responseInfo, responseXML) {
    if (!this.isIntegrationPossible || this.isNativeReporting) {
      return;
    }
    if (
      responseInfo.topicName === 'RadDesktop.LS_PLUGIN_LOAD_RESPONSE' &&
      responseInfo.sourceName === 'LineShaftPluginManager' &&
      responseInfo.moduleName === this.moduleName
    ) {
      this.handlePluginManagerLoadResponse(responseInfo);
    }
    if (
      responseInfo.topicName === 'LS_PLUGIN_LOAD_RESPONSE' &&
      responseInfo.sourceName === this.reportingService.getModuleName()
    ) {
      this.handlePluginLoadResponse(responseInfo);
    }
    this.reportingService.handleLineShaftMessage(responseInfo, responseXML);
  }

  private onViewerClosedResponseHandler() {
    const pluginLoadStatus = this.isPluginLoaded();
    if (!this.isIntegrationPossible || !pluginLoadStatus) {
      log.info(
        'onViewerClosedResponseHandler : skip viewerClosed event - isIntegrationPossible : ' +
          this.isIntegrationPossible +
          ', isPluginLoaded : ' +
          pluginLoadStatus
      );
      return;
    }
    this.reportingService.invokeViewerClosedCallback();
  }

  private onProcessReportingQueue() {
    const pluginLoadStatus = this.isPluginLoaded();
    if (!this.isIntegrationPossible || !pluginLoadStatus) {
      log.info(
        'onProcessReportingQueue : skip processQueue event - isIntegrationPossible : ' +
          this.isIntegrationPossible +
          ', isPluginLoaded : ' +
          pluginLoadStatus
      );
      return;
    }
    this.reportSupportService.processQueue();
  }

  private onDILoadTimeout(moduleName) {
    if (this.loadStatus !== appConstants.pluginLoadStatus.PluginLoadFailed && moduleName === this.moduleName) {
      log.error('onDILoadTimeout : timeout occurred disabling the dictation integration');
      this.notify.critical(
        this.translate.instant('di.desktopIntegration.critical.loadDictateFailed'),
        this.translate.instant('di.desktopIntegration.titleDictateDisabled')
      );
      this.loadStatus = appConstants.pluginLoadStatus.PluginLoadFailed;
      this.integrationStatusService.setReportingPluginStatus(true);
      this.integrationStatusService.setReportingHealthStatus(this.integrationStatusService.healthStatusEnum.failed);
    }
  }

  private updateExamAssociation(exam) {
    if (!this.checkIntegrationState('readAssociateExam')) {
      return;
    }
    if (this.reportingService) {
      this.reportingService.examAssociation(exam);
    }
  }

  closePlugin() {
    log.info('close reporting plugin');
    // Setting the Application termination status to true to skip the setcontext
    // response from the plugin
    this.reportSupportService.setApplicationTerminateStatus(true);
    this.reportSupportService.clearMessageWaitingForResponse();
    this.reportSupportService.clearMessageQueue();
    if (!this.isNativeReporting) {
      // Send the close xml
      this.reportingService.sendCloseXML();
      // Shows re-launch option pop up dialog
      this.reportingService.sendReportingRelogin();
    }
  }

  private sendLoadPluginXML() {
    // Load reporting plugin
    log.info('load reporting plugin');
    const loadXML = this.xmlTemplateManagerService.getPluginLoadXml(this.moduleName);
    const loadMessage = this.diService.getLSMessage(this.diService.getLSMessageType().LOAD, loadXML);
    loadMessage.responseSource = [
      this.reportingService.getModuleName(),
      this.diConfigService.UVIEWER_CONFIG.PLUGIN_MANAGER
    ];
    loadMessage.moduleName = this.moduleName;
    this.diService.loadPluginsInvokeAPIWrapper(loadMessage);
  }

  requestToMinimize() {
    if (this.checkIntegrationState() && this.reportingService && this.reportingService.requestToMinimize) {
      this.reportingService.requestToMinimize();
    }
  }

  requestToPreview(workItem: any) {
    if (this.checkIntegrationState() && this.reportingService && this.reportingService.preview) {
      this.reportingService.preview(workItem);
    }
  }

  resetIntegration() {
    this.reportingService.resetIntegration();
  }

  isNativeIntegration() {
    return this.isNativeReporting;
  }

  isReadyForAssociation() {
    if (this.reportingService) {
      if (this.reportingService.isReadyForAssociation) {
        return this.reportingService.isReadyForAssociation();
      }
      return true;
    }
    return false;
  }

  getReportBtnState(reportInfo: any) {
    const reportIntegrated = this.doReportingIntegration();
    let btnState = {
      isEnabled: reportIntegrated,
      label: this.translate.instant('report.lbl.report')
    };

    if (this.reportingService && this.reportingService.getReportBtnState) {
      btnState = this.reportingService.getReportBtnState(reportInfo);
    }

    return btnState;
  }

  releaseControl(item: any) {
    if (typeof this.reportingService.releaseControl === 'function') {
      log.info('releaseControl on', item);
      return this.reportingService.releaseControl(item);
    } else {
      return Promise.resolve(item);
    }
  }

  isReportPending(accNo: any) {
    return typeof this.reportingService.isReportPending === 'function'
      ? this.reportingService.isReportPending(accNo)
      : false;
  }

  getReportContent(examId: number) {
    if (this.isNativeIntegration) {
      return this.nprDataService.getReport(examId);
    }
  }

  getReportLifecycleInfo(reportId: number) {
    return this.nprDataService.getReportLifecycle(reportId);
  }

  //Filtering logic based on Digital Signature status
  private filterSupportedReportStatuses(statuses: any[]): any[] {
    if (!this.appConfigService.isDigitalSignatureEnabled()) {
      return statuses.filter(
        status => status.reportStatusId !== appConstants.WFMReportStatuses[appConstants.ReportStatus.Signed]
      );
    }
    return statuses;
  }

  private loadSupportedReportStatuses() {
    const reportType = this.diConfigService.DI_CONFIG.REPORTING_INTEGRATION_TYPE;
    if (reportType !== appConstants.reportingAppName.None) {
      log.debug('loadSupportedReportStatuses for reportType = ', reportType);
      this.nprDataService.getReferences('report-statuses?message=' + reportType).then(
        response => {
          let supportedReportStatus: any[] = this.searchFilterCriteriaService.updateSupportedReportStatus(response);
          supportedReportStatus = this.filterSupportedReportStatuses(supportedReportStatus);
          this.advanceSearchService.updateSupportedReportStatus(supportedReportStatus);
        },
        error => {
          log.error('Failed to load supported status list from Native PACS Reporting. Error:', error);
          const loadTimeout = setTimeout(() => {
            this.loadSupportedReportStatuses();
            clearTimeout(loadTimeout);
          }, 30_000);
        }
      );
    }
  }

  private listenersForLineShaftChannel() {
    this.lineShaftHandlerService.channelObservers.onReady.subscribe(startLoad => {
      if (startLoad) {
        log.info('LineShaft registration started');
        this.onDILoadPlugin();
      }
    });
    this.lineShaftHandlerService.channelObservers.onResponseMessage.subscribe(({ responseInfo, responseXML }) => {
      log.debug('LineShaft message processing', { responseInfo, responseXML });
      this.onHandleLineShaftMessage(responseInfo, responseXML);
    });
  }

  private broadcastListeners() {
    this.userPreferenceService.themeObserver$.asObservable().subscribe(lightsOn => this.switchTheme(lightsOn));
    this.messageService.on('diDisableIntegration').subscribe(() => {
      this.onDisableIntegration();
    });
    this.messageService.on('processQueue').subscribe(() => {
      this.onProcessReportingQueue();
    });
    this.messageService.on('viewerClosed').subscribe(() => {
      this.onViewerClosedResponseHandler();
    });
    this.messageService.on('diLoadTimeout').subscribe(moduleName => {
      this.onDILoadTimeout(moduleName);
    });
    this.messageService.on('examAssociation').subscribe(exam => {
      this.updateExamAssociation(exam);
    });
    this.messageService.on('closePlugin').subscribe(() => {
      this.closePlugin();
    });
    this.messageService.on('loadPlugin').subscribe(() => {
      this.sendLoadPluginXML();
    });
    this.messageService.on('resetPlugin').subscribe(() => {
      if (this.integrationStatusService.isReportActive()) {
        log.info('LineShaft restart - Resetting reporting plugin status');
        this.integrationStatusService.setReportingHealthStatus('init');
        this.integrationStatusService.setReportingPluginStatus(false);
        this.loadStatus = appConstants.pluginLoadStatus.None;
        this.reportSupportService.clearMessageWaitingForResponse();
        this.reportSupportService.clearMessageQueue();
        // Removing the reporting context since application is going to reconnect.
        this.reportContextService.removeContext(this.reportContextService.getCurrentRequestedProcId());
        // Set this flag to identify whether reload is happend due to LineShat restart.
        this.reportSupportService.setLineShaftTerminateStatus(true);
      }
    });
    this.messageService.on('refreshDXHub').subscribe(data => {
      this.refresh = data.refresh;
    });
    const preloadListener = this.messageService.on('preloadSearchCriteria.done').subscribe(() => {
      preloadListener.unsubscribe();
      this.loadSupportedReportStatuses();
    });
  }

  private afterEvents() {
    _.each(['preview', 'externalAccessTab'], actionName => {
      this.eventService.afterEvent(actionName, target => {
        const studyId = target.id;
        log.info('preview action - requestedProcedureId : ' + studyId);
        try {
          if (this.refresh) {
            this.refresh = false;
            return;
          }
          if (!this.checkIntegrationState('preview')) {
            return;
          }
          if (
            target.keys.source === 'desktop-integration' ||
            (target.keys.source === 'patient-history' && target.keys.comparisonAction === 'createOnly')
          ) {
            target.keys.comparisonAction = '';
            log.debug('skip preview action, source = desktop-integration ');
            return;
          }
          this.reportingService.preview(target);
        } catch (error) {
          log.error('failed preview study, Id' + studyId + ' with error: ' + error);
        }
      });
    });

    _.each(['read', 'takeControl'], actionName => {
      this.eventService.afterEvent(
        actionName,
        target => {
          try {
            if (this.refresh) {
              this.refresh = false;
              return;
            }
            if (!this.checkIntegrationState(actionName)) {
              if (!this.isAppLaunching()) {
                this.reportSupportService.releaseReadLock(target.id);
              }
              return;
            }
            if (target.keys.source === 'desktop-integration') {
              log.debug('skip read action, source = desktop-integration ');
              return;
            }
            if (target.keys && !target.keys.setContextByExternalApp && !target.keys.launchByExternalApp) {
              this.readActionHandler(target);
            }
          } catch (error) {
            log.error(
              'failed on read action handler - Id : ' +
                target.id +
                ' Acc: ' +
                target.keys.procedureAccessionNo +
                ' with error: ' +
                error
            );
          }
        },
        EventType.LOCAL
      );
    });

    this.eventService.afterEvent(Events.launchreadonly.name, target => {
      try {
        if (this.refresh) {
          this.refresh = false;
          return;
        }
        if (!this.checkIntegrationState('launchreadonly')) {
          return;
        }
        if (!target.keys.setContextByExternalApp) {
          PerformanceMonitorService.clear(PerformanceSource.REPORT);
          this.reportingService.launchReadonly(target);
        }
      } catch (error) {
        log.error('failed on launchreadonly action handler with ' + error);
      }
    });

    this.eventService.afterEvent(
      Events.closediscard.name,
      WorkItem => {
        this.closeDiscardReport(WorkItem);
      },
      EventType.LOCAL
    );

    [Events.autoAdvanceSkipAssign.name, Events.autoAdvanceSkipUnAssign.name].forEach(actionName => {
      this.eventService.beforeEvent(actionName, (lifecycle, WorkItem) => {
        this.closeDiscardReport(WorkItem);
        lifecycle.done();
      });
    });

    [Events.closeExternalTab.name, Events.closeviewer.name].forEach(actionName => {
      this.eventService.afterEvent(actionName, WorkItem => {
        this.closeDiscardReport(WorkItem);
      });
    });

    this.eventService.afterEvent(Events.launchDictation.name, target => {
      this.messageService.broadcast('onLaunchDictation.success');
      if (!this.checkIntegrationState('launchDictation')) {
        if (!this.isAppLaunching()) {
          this.reportSupportService.releaseReadLock(target.id);
        }
        return;
      }
      if (!this.isNativeIntegration() && _.isEmpty(target.keys.procedureAccessionNo)) {
        PerformanceMonitorService.clear(PerformanceSource.REPORT);
        log.error('launchDictation : procedureAccessionNo is empty, cannot open the report.');
        this.notify.warning(
          this.translate.instant('di.desktopIntegration.warning.emptyAccession'),
          this.translate.instant('di.desktopIntegration.title')
        );
        this.reportSupportService.releaseReadLock(target.id);
        return;
      }
      this.reportingService.launchDictation(target);
    });

    this.eventService.afterEvent(Events.previewReport.name, target => {
      if (!this.checkIntegrationState('previewReport')) {
        return;
      }
      if (!this.isNativeIntegration() && _.isEmpty(target.keys.procedureAccessionNo)) {
        log.error('previewReport : procedureAccessionNo is empty, cannot preview the report.');
        this.notify.warning(
          this.translate.instant('di.desktopIntegration.warning.emptyAccession'),
          this.translate.instant('di.desktopIntegration.title')
        );
        return;
      }
      this.reportingService.previewReport(target);
    });

    _.each([Events.close.name, Events.clear.name], actionName => {
      this.eventService.afterEvent(
        actionName,
        context => {
          this.resetApplicationState(context);
        },
        EventType.LOCAL
      );
    });
  }

  private beforeEvents() {
    this.messageService.on('resetIntegration').subscribe(integrationType => {
      if (integrationType === 'Report') {
        this.resetIntegration();
      }
    });
  }

  removeExamFromAssociation(examId) {
    if (this.reportingService && this.reportingService.removeExamFromAssociation) {
      this.reportingService.removeExamFromAssociation(examId);
    }
  }

  switchTheme(lightsOn: boolean) {
    const theme = lightsOn ? 'light' : 'dark';
    if (this.isNativeReporting) {
      this.reportingService.setTheme(theme);
    }

    const reportFrame: any = document.getElementById('primary-exam-report-body-html');
    if (typeof reportFrame?.contentWindow?.setTheme === 'function') {
      reportFrame.contentWindow.setTheme(theme);
    }

    const comparisonFrame: any = document.getElementById('irccFrame');
    if (typeof comparisonFrame?.contentWindow?.setTheme === 'function') {
      comparisonFrame.contentWindow.setTheme(theme);
    }
  }

  getReportStatus(examIds) {
    return this.nprDataService.getReportStatus(examIds);
  }

  private isAppLaunching() {
    return (
      this.reportingService?.getDictationStatus() === appConstants.dictationIntegrationStatus.LaunchApplicationInitiated
    );
  }
}
